import {
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Task } from "../../pojo/Task";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AccessLevels } from "../../pojo/Fls";
import { FlsService } from "../../services/fls.service";
import { UserprefService } from "../../services/userpref.service";
import { TaskChange, TaskService } from "../../services/task/task.service";
import { TaskTiersService } from "../../services/task/task.tiers.service";
import { TaskActionCellFactory } from "../../services/task/task.list.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25TaskCompetingConfirmationComponent } from "./s25.task.competing.confirmation.component";
import { S25FadingSuccessMsgComponent } from "../s25-fading-success-msg/s25.fading.success.msg.component";
import { WorkflowService } from "../workflow/service/workflow.service";
import { TaskCompetingUtil } from "./task.competing.util";

@TypeManagerDecorator("s25-ng-task-action")
@Component({
    selector: "s25-ng-task-action",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static [text]="''"></s25-ng-loading-inline-static>
        }
        @if (!isLoading()) {
            <div>
                <!-- Show text when user can't take an action (approve/deny AND blocked by tier) cancel lives outside of tiers-->
                @if (!hasCancelPerms && (!hasApproveDenyPerms || (taskBlocked && workflowChained))) {
                    <span class="ngItalic ngTaskActionText">{{ itemText || "None" }}</span>
                } @else if (hasApprove) {
                    <div class="ngTaskActionButtons c-modal-actions-flex">
                        @if (!showConfirm) {
                            <div
                                class="taskStateChoice taskStatePositive ngCpointer ngNoSelect ngInlineBlock"
                                (click)="approve()"
                                (keydown.enter)="approve()"
                                tabindex="0"
                            >
                                <div>
                                    <div class="sprite_approve ngInlineBlock">
                                        <s25-ng-icon [type]="'thumbsUp'"></s25-ng-icon>
                                    </div>
                                    <div class="ngInlineBlock">
                                        {{ approveText + (itemCount > 1 ? " All" : "") }}
                                    </div>
                                </div>
                            </div>
                        }
                        @if (hasDeny && !showConfirm) {
                            <div
                                class="taskStateChoice taskStateNegative ngCpointer ngNoSelect ngInlineBlock"
                                (click)="deny()"
                                (keydown.enter)="deny()"
                                tabindex="0"
                            >
                                <div class="sprite_deny ngInlineBlock">
                                    <s25-ng-icon [type]="'thumbsDown'"></s25-ng-icon>
                                </div>
                                <div class="ngInlineBlock">
                                    {{ denyText + (itemCount > 1 ? " All" : "") }}
                                </div>
                            </div>
                        }
                        @if (hasRequestChanges && !showConfirm) {
                            <div
                                class="taskStateChoice taskStateWarning ngCpointer ngNoSelect ngInlineBlock"
                                (click)="showRequestChangesModal = true"
                                (keydown.enter)="showRequestChangesModal = true"
                                tabindex="0"
                            >
                                <div class="ngInlineBlock">
                                    <s25-ng-icon [type]="'edit'"></s25-ng-icon>
                                </div>
                                <div class="ngInlineBlock">Request Changes</div>
                            </div>
                        }
                        @if (showConfirm) {
                            <div>
                                <div>Are you sure you want to cancel this event?</div>
                                <button
                                    class="aw-button aw-button--primary"
                                    (click)="approve()"
                                    (keydown.enter)="approve()"
                                >
                                    Yes
                                </button>
                                <button
                                    class="aw-button aw-button--outline"
                                    (click)="dismissConfirm()"
                                    (keydown.enter)="dismissConfirm()"
                                >
                                    No
                                </button>
                            </div>
                        }
                    </div>
                } @else if (hasCancelPerms) {
                    <button
                        class="aw-button aw-button--primary"
                        (click)="cancel()"
                        (keydown.enter)="cancel()"
                        tabindex="0"
                    >
                        Cancel
                    </button>
                }
                @if (updateFailed) {
                    <div class="ngRed taskFailureMessage">Failed to update task</div>
                }
                <s25-ng-fading-success-msg [message]="successMessage"></s25-ng-fading-success-msg>
                <s25-task-competing-confirmation
                    [show]="showWaitlistConfirm()"
                    [message]="competingMessage()"
                    [count]="competesCount()"
                    [disabled]="isLoading()"
                    [inline]="competingDisplayMode === 'inline'"
                    (confirm)="handleCompetingConfirm($event)"
                    (cancel)="dismissWaitlistConfirm()"
                ></s25-task-competing-confirmation>
            </div>
        }

        <!-- Request Changes Modal -->
        @if (showRequestChangesModal) {
            <div class="modal-overlay" (click)="hideRequestChangesModal()">
                <div class="modal-content" (click)="$event.stopPropagation()">
                    <h3>Request Changes</h3>
                    <div class="form-group">
                        <label for="changesText">Changes Required:</label>
                        <textarea
                            id="changesText"
                            [(ngModel)]="requestedChangesText"
                            rows="4"
                            class="form-control"
                            placeholder="Describe what changes are needed..."
                        ></textarea>
                    </div>
                    <div class="modal-actions">
                        <button
                            class="aw-button aw-button--primary"
                            (click)="requestChanges()"
                            [disabled]="!requestedChangesText.trim() || isLoading()"
                        >
                            Submit Request
                        </button>
                        <button
                            class="aw-button aw-button--outline"
                            (click)="hideRequestChangesModal()"
                            [disabled]="isLoading()"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        }
    `,
    styles: `
        .taskStateChoice {
            overflow-wrap: anywhere;
        }

        .ngTaskActionButtons {
            display: flex;
            column-gap: 1em;
            row-gap: 0.5em;
        }

        .taskStateWarning {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            color: #856404;
            padding: 8px 12px;
            border-radius: 4px;
        }

        .taskStateWarning:hover {
            background-color: #ffeaa7;
        }

        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: white;
            padding: 20px;
            border-radius: 8px;
            min-width: 400px;
            max-width: 600px;
        }

        .modal-content h3 {
            margin: 0 0 15px 0;
            color: #333;
        }

        .form-group {
            margin-bottom: 15px;
        }

        .form-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-family: inherit;
            box-sizing: border-box;
        }

        .modal-actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
        }
    `,
    standalone: true,
    imports: [
        FormsModule,
        S25IconComponent,
        S25LoadingInlineModule,
        S25TaskCompetingConfirmationComponent,
        S25FadingSuccessMsgComponent,
    ],
})
export class S25TaskActionComponent implements OnInit {
    @Input() taskId: number;
    @Input() taskBlocked: boolean;
    @Input() itemCount: number;
    @Input() taskState: Task.State;
    @Input() taskType: Task.Id;
    @Input() requesterId: number;
    @Input() eventId: number;
    @Input() assignedToId: number;
    @Input() todoType: Task.Todo.Type;
    @Input() todoSubType: Task.SubTypes;
    @Input() objectType: number; // Resource/Location
    @Input() objectId: number; // The id of the requested object
    @Input() competes?: Task.SpaceCompete[]; // waitlist
    @Input() reservationId?: number;
    @Input() eventName?: string;
    @Input() competingDisplayMode: "inline" | "modal" = "inline"; // Display confirmation inline or as modal overlay

    @Output() stateChange = new EventEmitter<Task.State>();

    showConfirm = false;
    init = false;
    isLoading = signal<boolean>(false);
    hasApprove = false;
    hasDeny = false;
    hasRequestChanges = false;
    hasCancelPerms: boolean = false;
    workflowChained: boolean;
    approveText: string;
    denyText: string;
    hasApproveDenyPerms: boolean = false;
    itemText: string;
    userId: number;
    updateFailed: boolean;

    // Workflow-specific properties
    showRequestChangesModal = false;
    requestedChangesText = "";
    successMessage = signal<string>("");

    showWaitlistConfirm = signal<boolean>(false);
    clickWaitlistApproved = signal<boolean>(false);
    competesCount = computed<number>(() => this.pendingCompetingInfo()?.count ?? 0);
    competingMessage = computed<string>(() => TaskCompetingUtil.getConfirmationMessage(this.competesCount()));
    pendingCompetingInfo = signal<TaskCompetingUtil.CompetingTasksInfo | null>(null);

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    async ngOnInit() {
        const [fls, userId, workflowPref, eventOls] = await Promise.all([
            FlsService.getFls(),
            UserprefService.getContactId(),
            TaskTiersService.isWorkflowChained(),
            this.eventId && TaskActionCellFactory.getActionCellsOls([this.eventId]),
        ]);

        this.userId = userId;
        const editAccess = S25Util.propertyGetParentWithChildValue(eventOls, "object_id", this.eventId)?.access_level;
        this.hasCancelPerms =
            editAccess &&
            editAccess !== AccessLevels.None &&
            fls.TASK_CANCEL === AccessLevels.Full &&
            this.taskType === Task.Ids.Assign &&
            this.taskState === Task.States.InProgress &&
            this.requesterId === this.userId;

        this.workflowChained = workflowPref;
        this.update();
    }

    update = () => {
        this.hasApproveDenyPerms = false;
        this.hasApprove = false;
        this.hasDeny = false;
        if (
            this.taskState === Task.States.InProgress &&
            ([Task.Todo.Types.NeedsSpace, Task.Todo.Types.HeadCountAdj].includes(this.todoType) ||
                this.assignedToId === this.userId)
        ) {
            //if task is in-progress AND (admin OR assigned to user)
            this.hasApproveDenyPerms = true;
            this.denyText = "Deny";
            if (this.taskType === Task.Ids.FYI) this.approveText = "Acknowledge";
            else if (this.taskType === Task.Ids.Assign) this.approveText = "Assign";
            else if (this.taskType === Task.Ids.UnAssign) this.approveText = "UnAssign";
            else if (this.taskType === Task.Ids.Todo) {
                this.approveText = "Complete";
                this.denyText = "Ignore";
            } else this.approveText = "Approve";
        } else if (this.taskState === Task.States.Various && this.assignedToId === this.userId) {
            //if invalid-state and assigned to user
            this.itemText = "See Event";
        } else if (this.taskState === Task.States.Declined && this.taskType === Task.Ids.Authorization) {
            // ANG-1418 -- notify tasks can be approved after denial
            this.hasApproveDenyPerms = true;
            this.approveText = "Approve Previously Denied";
        } else if (this.taskState === Task.States.Cancelled) {
            this.hasCancelPerms = false;
        } else if (this.taskState === Task.States.RequestedChanges) {
            // For requested changes state, show limited actions
            this.itemText = "Changes Requested";
        } else {
            //else: no actions for user...
            this.itemText = "None";
        }

        if (this.hasApproveDenyPerms) {
            this.hasDeny = this.hasApproveDenyPerms && this.taskType !== Task.Ids.FYI;

            //ANG-1418 - Notification tasks can be approved after denial
            if (this.taskState === Task.States.Declined) {
                this.hasApprove = Task.Ids.Authorization === this.taskType;
                this.hasDeny = false;
            } else this.hasApprove = true;
        }

        // Add "Request Changes" option for workflow todos
        if (
            this.todoSubType === Task.SubTypes.workflow &&
            this.taskState === Task.States.InProgress &&
            this.hasApproveDenyPerms
        ) {
            this.hasRequestChanges = true;
        }

        if (this.workflowChained && this.taskBlocked) {
            this.hasDeny = false;
            this.hasApprove = false;
            this.hasRequestChanges = false;
        }
        this.cd.detectChanges();
    };

    approve = async () => {
        if (
            !this.showConfirm &&
            this.eventId &&
            (this.todoType || this.taskType === Task.Ids.Todo) &&
            this.todoSubType === Task.SubTypes.cancel
        ) {
            this.showConfirm = true;
            this.cd.detectChanges();
            return;
        }

        // Check for competing tasks
        if (this.competes?.length > 0 && !this.clickWaitlistApproved()) {
            const competingInfo = await TaskCompetingUtil.analyzeCompetes(this.competes);

            if (competingInfo.hasCompetingTasks) {
                this.pendingCompetingInfo.set(competingInfo);
                this.clickWaitlistApproved.set(true);
                this.showWaitlistConfirm.set(true);
                this.cd.detectChanges();
                return;
            }
        }

        this.showConfirm = false;
        this.cd.detectChanges();
        return this.changeTask(Task.States.Assigned, false);
    };

    dismissConfirm = () => {
        this.showConfirm = false;
        this.cd.detectChanges();
    };

    deny = () => {
        return this.changeTask(Task.States.Denied);
    };

    cancel = () => {
        return this.changeTask(Task.States.Cancelled);
    };

    changeTask = async (newState: Task.State, shouldDenyCompeting: boolean = false) => {
        this.updateFailed = false;
        const taskData: TaskChange = {
            itemId: this.taskId,
            itemTypeId: this.taskType,
            eventId: this.eventId,
            todoType: this.todoType,
            isTodo: this.taskType === Task.Ids.Todo,
            todoSubType: this.todoSubType,
            itemStateId: this.taskState,
            itemCount: this.itemCount,
            objectType: this.objectType,
            objectId: this.objectId,
        };
        this.isLoading.set(true);
        this.cd.detectChanges();

        // Use centralized utility for approval with optional competing denial
        if (newState === Task.States.Assigned && this.pendingCompetingInfo()) {
            const result = await TaskCompetingUtil.approveWithOptionalDenial(
                [taskData],
                this.pendingCompetingInfo()!,
                shouldDenyCompeting,
            );

            if (result.success) {
                this.taskState = newState;
                this.stateChange.emit(this.taskState);
                this.update();
            } else {
                this.updateFailed = true;
                S25Util.showError(result.error, "Something went wrong when updating the task");
            }

            this.pendingCompetingInfo.set(null);
            this.isLoading.set(false);
            this.cd.detectChanges();
            return;
        }

        // Standard task change without competing logic
        const [results, err] = await S25Util.Maybe(TaskService.changeTask(taskData, newState));
        if (err) {
            this.isLoading.set(false);
            this.cd.detectChanges();
            return S25Util.showError(err, "Something went wrong when updating the task");
        }
        const result = results[0];
        if (result.success) {
            this.taskState = newState;
        }
        switch (result.error) {
            case "Deny":
                this.taskState = this.itemCount === 1 ? Task.States.Denied : newState;
                break;
            case "Failed":
                this.updateFailed = true;
                break;
        }
        this.stateChange.emit(this.taskState);

        this.update();

        this.isLoading.set(false);
        this.cd.detectChanges();
    };

    hideRequestChangesModal = () => {
        this.showRequestChangesModal = false;
        this.requestedChangesText = "";
        this.cd.detectChanges();
    };

    requestChanges = async () => {
        if (!this.requestedChangesText.trim()) {
            return;
        }

        this.updateFailed = false;
        this.isLoading.set(true);
        this.cd.detectChanges();

        try {
            // Use the todoId directly - much simpler than trying to get submissionId
            const response = await WorkflowService.requestChangesFromTodo(
                this.taskId,
                this.requestedChangesText.trim(),
            );

            if (response?.message?.includes("success") || response?.messageCode?.toLowerCase() === "success") {
                // Successfully requested changes - hide modal and show success message
                this.hideRequestChangesModal();
                this.successMessage.set("Changes requested successfully");
                // Emit state change to refresh the parent component
                this.stateChange.emit(this.taskState);
            } else {
                this.updateFailed = true;
                S25Util.showError("Failed to request changes: " + (response.message || "Unknown error"));
            }
        } catch (error) {
            this.updateFailed = true;
            S25Util.showError(error, "Error requesting changes");
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    };

    /**
     * Handle user's response to competing tasks confirmation
     */
    handleCompetingConfirm = async (shouldDenyCompeting: boolean) => {
        this.showWaitlistConfirm.set(false);
        this.clickWaitlistApproved.set(false);
        await this.changeTask(Task.States.Assigned, shouldDenyCompeting);
    };

    /**
     * Dismiss competing tasks confirmation
     */
    dismissWaitlistConfirm = () => {
        this.showWaitlistConfirm.set(false);
        this.clickWaitlistApproved.set(false);
        this.pendingCompetingInfo.set(null);
        this.cd.detectChanges();
    };
}
